// The browser's answers: what the app did everywhere before there was an interface to name it.
// Nothing here is new behaviour — each method is the code that used to sit in the screen that
// called it, moved so that a shell can answer differently.
import type { PickedFile, Platform } from '../platform.js';

const read = (file: File): Promise<PickedFile> =>
  file.arrayBuffer().then((b) => ({ name: file.name, mime: file.type || 'application/octet-stream', bytes: new Uint8Array(b) }));

/** A file input made, used and thrown away: the browser has no other way to ask. */
function ask(accept: string, multiple: boolean): Promise<File[]> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = accept;
    input.multiple = multiple;
    // A person who closes the picker without choosing fires nothing at all in most browsers,
    // so the promise is settled by whichever comes first: a choice, or the window coming back.
    const done = (files: File[]): void => { window.removeEventListener('focus', back); resolve(files); };
    const back = (): void => { setTimeout(() => done([]), 400); };
    input.onchange = () => done([...(input.files ?? [])]);
    window.addEventListener('focus', back, { once: true });
    input.click();
  });
}

const blobUrl = (f: PickedFile): string => URL.createObjectURL(new Blob([f.bytes as BlobPart], { type: f.mime }));

export const web: Platform = {
  kind: 'web',

  loadSecrets: async () => {
    const out = new Map<string, string>();
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k?.startsWith('cb.')) out.set(k, localStorage.getItem(k) ?? '');
      }
    } catch { /* a private window with storage off: the app still runs, it just forgets */ }
    return out;
  },
  writeSecret: (key, value) => {
    try { if (value === null) localStorage.removeItem(key); else localStorage.setItem(key, value); }
    catch { /* nothing to be done, and nothing to say: the app works, it will ask again */ }
  },
  forgetSecrets: () => {
    try { for (const k of Object.keys(localStorage)) if (k.startsWith('cb.')) localStorage.removeItem(k); }
    catch { /* as above */ }
  },

  // No scanner of its own: the camera work lives in the screen that shows the picture, which
  // cannot be hidden behind a promise.
  hasScanner: false,
  scanQr: async () => null,
  hasCamera: async () => Boolean(navigator.mediaDevices?.getUserMedia),

  share: async (what) => {
    if (!navigator.share) return false;
    const files = what.files?.map((f) => new File([f.bytes as BlobPart], f.name, { type: f.mime }));
    const payload = { ...(what.title ? { title: what.title } : {}), ...(what.text ? { text: what.text } : {}),
      ...(what.url ? { url: what.url } : {}), ...(files && files.length > 0 ? { files } : {}) };
    if (files && files.length > 0 && !navigator.canShare?.(payload)) return false;
    try { await navigator.share(payload); return true; }
    catch { return false; }        // dismissed, which is not a failure worth a word
  },
  copy: async (text) => {
    try { await navigator.clipboard.writeText(text); return true; } catch { return false; }
  },

  pickFiles: async (accept, limit) => {
    const files = await ask(accept, limit > 1);
    return Promise.all(files.slice(0, limit).map(read));
  },
  saveFile: async (file) => {
    const url = blobUrl(file);
    const a = document.createElement('a');
    a.href = url; a.download = file.name;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  },
  openFile: async (file) => { window.open(blobUrl(file), '_blank', 'noopener'); },

  watchUpdates: (onReady) => {
    const ready = (): void => onReady('reload');
    window.addEventListener('chilavubook:update-ready', ready);
    return () => window.removeEventListener('chilavubook:update-ready', ready);
  },
  applyUpdate: () => location.reload(),

  openedWith: async () => location.href,
  onOpenedWith: () => () => {},     // a link in a browser is a page load, not an event
};

/** What a web build uses. The name is the same in native.ts, and the build points the
 *  import at one file or the other — which is how neither bundle carries the other's code. */
export { web as impl };
